import type { Edge, Node } from "@xyflow/react"

export type WorkflowOperation = {
  type: string
  [key: string]: unknown
}

export type ApplyOperationsResult = {
  nodes: Node[]
  edges: Edge[]
  errors: string[]
  metadataUpdate?: { title?: string; defaultProjectId?: string | null }
}

const INVALID_HANDLE_VALUES = new Set(["", "null", "undefined"])

function isValidHandleValue(value: unknown): value is string {
  return typeof value === "string" && !INVALID_HANDLE_VALUES.has(value)
}

function getNextPosition(nodes: Node[]): { x: number; y: number } {
  const x = Math.max(0, nodes.length * 60)
  const y = Math.max(0, (nodes.length % 6) * 80)
  return { x, y }
}

function createUniqueId(prefix: string, existingIds: Set<string>): string {
  // Generate sequential IDs like "llm-1", "llm-2", "agent-1", etc.
  // This matches what AI models expect when they don't provide explicit node_id values.
  let counter = 1
  let candidate = `${prefix}-${counter}`

  while (existingIds.has(candidate)) {
    counter++
    candidate = `${prefix}-${counter}`
  }

  return candidate
}

function resolveMappedId(value: unknown, idRemap: Map<string, string>): string | undefined {
  if (typeof value !== "string" || !value) return undefined
  return idRemap.get(value) ?? value
}

function getDuplicateRequestedId(
  requestedId: string | undefined,
  existingIds: Set<string>
): string | undefined {
  if (typeof requestedId !== "string") return undefined
  return existingIds.has(requestedId) ? requestedId : undefined
}

export function applyWorkflowOperations(
  operations: WorkflowOperation[],
  initialNodes: Node[],
  initialEdges: Edge[]
): ApplyOperationsResult {
  let nextNodes: Node[] = structuredClone(initialNodes)
  let nextEdges: Edge[] = structuredClone(initialEdges)
  const errors: string[] = []
  let metadataUpdate: ApplyOperationsResult["metadataUpdate"] = undefined
  const nodeIds = new Set(nextNodes.map((node) => node.id))
  const edgeIds = new Set(nextEdges.map((edge) => edge.id))
  const nodeIdRemap = new Map<string, string>()
  const edgeIdRemap = new Map<string, string>()

  for (const op of operations) {
    if (typeof op !== "object") continue
    const opType = op.type
    if (typeof opType !== "string") continue

    if (opType === "add_node") {
      const nodeType = op.node_type
      if (typeof nodeType !== "string" || !nodeType) {
        errors.push("add_node missing node_type")
        continue
      }

      const providedNodeId = op.node_id
      const requestedNodeId =
        typeof providedNodeId === "string" && providedNodeId ? providedNodeId : undefined
      const duplicateRequestedNodeId = getDuplicateRequestedId(requestedNodeId, nodeIds)
      let nodeId: string
      if (duplicateRequestedNodeId) {
        nodeId = createUniqueId(duplicateRequestedNodeId, nodeIds)
        nodeIdRemap.set(duplicateRequestedNodeId, nodeId)
      } else if (requestedNodeId) {
        nodeId = requestedNodeId
      } else {
        nodeId = createUniqueId(nodeType, nodeIds)
      }
      nodeIds.add(nodeId)

      // Map the label to the generated ID so edges can reference nodes by label
      const label = op.label
      if (typeof label === "string" && label && label !== nodeId && !nodeIds.has(label)) {
        nodeIdRemap.set(label, nodeId)
      }

      const data = op.data
      const position = op.position

      const resolvedData =
        data && typeof data === "object" && !Array.isArray(data)
          ? {
              label: typeof label === "string" ? label : nodeType,
              ...(data as Record<string, unknown>),
            }
          : { label: typeof label === "string" ? label : nodeType }

      const resolvedPosition =
        position &&
        typeof position === "object" &&
        "x" in (position as Record<string, unknown>) &&
        "y" in (position as Record<string, unknown>)
          ? (position as { x: number; y: number })
          : getNextPosition(nextNodes)

      // Handle parent_id for loop container children
      const parentId = resolveMappedId(op.parent_id, nodeIdRemap)
      const hasParent = typeof parentId === "string" && parentId

      // Validate parent exists if specified
      if (hasParent) {
        const parentNode = nextNodes.find((n) => n.id === parentId)
        if (!parentNode) {
          errors.push(`add_node parent_id "${parentId}" not found`)
          continue
        }
        if (parentNode.type !== "loopContainer") {
          errors.push(`add_node parent_id "${parentId}" is not a loopContainer`)
          continue
        }
      }

      const newNode: Node = {
        id: nodeId,
        type: nodeType,
        position: resolvedPosition,
        data: resolvedData,
        ...(hasParent && { parentId, extent: "parent" as const }),
      }

      nextNodes.push(newNode)

      // Update parent's childNodes array if adding to a loop
      if (hasParent) {
        nextNodes = nextNodes.map((node) =>
          node.id === parentId
            ? {
                ...node,
                data: {
                  ...(node.data as Record<string, unknown>),
                  childNodes: [
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    ...(((node.data as Record<string, unknown>).childNodes as string[]) ?? []),
                    nodeId,
                  ],
                },
              }
            : node
        )
      }

      continue
    }

    if (opType === "remove_node") {
      const nodeId = resolveMappedId(op.node_id, nodeIdRemap)
      if (typeof nodeId !== "string" || !nodeId) continue

      // Find the node to check if it has a parent
      const nodeToRemove = nextNodes.find((n) => n.id === nodeId)
      const nodeParentId = nodeToRemove?.parentId

      nextNodes = nextNodes.filter((node) => node.id !== nodeId)
      nextEdges = nextEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)

      // Remove from parent's childNodes array if it had a parent
      if (nodeParentId) {
        nextNodes = nextNodes.map((node) =>
          node.id === nodeParentId
            ? {
                ...node,
                data: {
                  ...(node.data as Record<string, unknown>),
                  childNodes:
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    (((node.data as Record<string, unknown>).childNodes as string[]) ?? []).filter(
                      (id) => id !== nodeId
                    ),
                },
              }
            : node
        )
      }

      continue
    }

    if (opType === "update_node_data") {
      const nodeId = resolveMappedId(op.node_id, nodeIdRemap)
      const data = op.partial_data ?? op.data
      if (typeof nodeId !== "string" || !nodeId) continue
      if (!data || typeof data !== "object" || Array.isArray(data)) continue
      nextNodes = nextNodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...(node.data as Record<string, unknown>),
                ...(data as Record<string, unknown>),
              },
            }
          : node
      )
      continue
    }

    if (opType === "add_edge") {
      const source = resolveMappedId(op.source, nodeIdRemap)
      const target = resolveMappedId(op.target, nodeIdRemap)
      if (typeof source !== "string" || typeof target !== "string" || !source || !target) {
        errors.push("add_edge missing source/target")
        continue
      }

      const edgeIdRaw = op.edge_id
      const requestedEdgeId = typeof edgeIdRaw === "string" && edgeIdRaw ? edgeIdRaw : undefined
      const duplicateRequestedEdgeId = getDuplicateRequestedId(requestedEdgeId, edgeIds)
      let edgeId: string
      if (duplicateRequestedEdgeId) {
        edgeId = createUniqueId(duplicateRequestedEdgeId, edgeIds)
        edgeIdRemap.set(duplicateRequestedEdgeId, edgeId)
      } else if (requestedEdgeId) {
        edgeId = requestedEdgeId
      } else {
        edgeId = createUniqueId(`${source}-${target}`, edgeIds)
      }
      edgeIds.add(edgeId)

      const sourceHandle = op.source_handle
      const targetHandle = op.target_handle
      const label = op.label

      const hasLabel = typeof label === "string" && label.length > 0
      const newEdge: Edge = {
        id: edgeId,
        source,
        target,
        ...(isValidHandleValue(sourceHandle) ? { sourceHandle } : {}),
        ...(isValidHandleValue(targetHandle) ? { targetHandle } : {}),
        ...(hasLabel ? { type: "labeled", data: { label } } : {}),
      }

      nextEdges.push(newEdge)
      continue
    }

    if (opType === "remove_edge") {
      const edgeId = resolveMappedId(op.edge_id, edgeIdRemap)
      if (typeof edgeId !== "string" || !edgeId) continue
      nextEdges = nextEdges.filter((edge) => edge.id !== edgeId)
      continue
    }

    if (opType === "set_metadata") {
      const title = op.title
      const defaultProjectId = op.default_project_id

      metadataUpdate = {
        ...(typeof title === "string" ? { title } : {}),
        ...(typeof defaultProjectId === "string" || defaultProjectId === null
          ? { defaultProjectId: defaultProjectId as string | null }
          : {}),
      }
      continue
    }
  }

  return { nodes: nextNodes, edges: nextEdges, errors, metadataUpdate }
}
